import React, { useEffect, useState } from 'react';
import { ChromePicker, ColorResult } from 'react-color';
import './ColorWheelPicker.css';

// Glow mode emits a colour + intensity pair instead of a plain hex string.
export type GlowValue = {
  color: string;
  intensity: number; // 0–100
};

type BaseProps = {
  ariaLabel?: string;
  modified?: boolean;
  disableAlpha?: boolean;
};

// Solid-colour mode (default): value/onChange speak plain hex strings and an
// optional preset swatch row sits beside the custom-picker trigger.
type SolidColorProps = BaseProps & {
  glow?: false;
  value: string;
  onChange: (hex: string) => void;
  presets?: string[];
};

// Glow mode: value/onChange speak { color, intensity } and the modal gains an
// intensity slider + number field beneath the colour picker. No preset row.
type GlowProps = BaseProps & {
  glow: true;
  value: GlowValue;
  onChange: (value: GlowValue) => void;
  presets?: never;
};

type ColorWheelPickerProps = SolidColorProps | GlowProps;

const GLOW_MIN = 0;
const GLOW_MAX = 100;
// Largest blur radius (px) used to preview the glow on the round trigger swatch.
const TRIGGER_GLOW_MAX_BLUR = 14;

function clampIntensity(value: number): number {
  if (Number.isNaN(value)) return GLOW_MIN;
  return Math.min(GLOW_MAX, Math.max(GLOW_MIN, Math.round(value)));
}

// Box-shadow that previews a glow of `color` at `intensity` (0–100) on the
// trigger swatch. Returns undefined at 0 so the swatch keeps its default
// (hover / modified) shadow.
function triggerGlowShadow(color: string, intensity: number): string | undefined {
  if (intensity <= 0) return undefined;
  const ratio = intensity / GLOW_MAX;
  const blur = ratio * TRIGGER_GLOW_MAX_BLUR;
  const spread = ratio * (TRIGGER_GLOW_MAX_BLUR / 4);
  return `0 0 ${blur.toFixed(1)}px ${spread.toFixed(1)}px ${color}`;
}

export default function ColorWheelPicker(props: ColorWheelPickerProps) {
  const { ariaLabel, modified = false, disableAlpha = true } = props;
  const isGlow = props.glow === true;

  // Normalise the incoming value into a colour + intensity pair so the modal
  // can treat both modes uniformly. Intensity is unused in solid mode.
  const currentColor = isGlow ? props.value.color : props.value;
  const currentIntensity = isGlow ? clampIntensity(props.value.intensity) : GLOW_MAX;

  const [open, setOpen] = useState(false);
  const [draftColor, setDraftColor] = useState(currentColor);
  // Kept as text so the number field can be cleared / partially typed; the
  // slider and the committed value derive a clamped number from it.
  const [intensityText, setIntensityText] = useState(String(currentIntensity));

  const draftIntensity = clampIntensity(Number(intensityText === '' ? 0 : intensityText));

  // Keep drafts in sync with external value changes while the modal is closed.
  useEffect(() => {
    if (!open) {
      setDraftColor(currentColor);
      setIntensityText(String(currentIntensity));
    }
  }, [currentColor, currentIntensity, open]);

  const handleOpen = () => {
    setDraftColor(currentColor);
    setIntensityText(String(currentIntensity));
    setOpen(true);
  };

  const handleCancel = () => {
    setDraftColor(currentColor);
    setIntensityText(String(currentIntensity));
    setOpen(false);
  };

  const handleConfirm = () => {
    if (props.glow) {
      props.onChange({ color: draftColor, intensity: draftIntensity });
    } else {
      props.onChange(draftColor);
    }
    setOpen(false);
  };

  const handlePickerChange = (color: ColorResult) => setDraftColor(color.hex);

  const handleIntensitySlider = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIntensityText(String(clampIntensity(Number(e.target.value))));
  };

  // Allow transient empty / partial input while typing; normalise on blur.
  const handleIntensityText = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIntensityText(e.target.value);
  };

  const handleIntensityBlur = () => {
    setIntensityText(String(draftIntensity));
  };

  // Selecting a preset commits immediately (solid mode only).
  const handlePreset = (preset: string) => {
    if (!props.glow) props.onChange(preset);
  };

  // Close on Escape. Closing resets the drafts via the sync effect above (it
  // runs whenever `open` becomes false), so this only needs `open` as a dep —
  // no reference to handleCancel, which keeps exhaustive-deps satisfied.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Preset swatch row only applies to solid mode.
  const presets = props.glow ? undefined : props.presets;
  const isCustomSelected = presets ? !presets.includes(currentColor) : true;

  // In glow mode, intensity 0 means "no glow set" — render the trigger as an
  // empty (no-colour) swatch instead of a solid black circle.
  const isGlowOff = isGlow && currentIntensity <= 0;

  const triggerStyle: React.CSSProperties = isGlowOff
    ? {}
    : isGlow
      ? { backgroundColor: currentColor, boxShadow: triggerGlowShadow(currentColor, currentIntensity) }
      : isCustomSelected
        ? { backgroundColor: currentColor }
        : {};

  const pickerTrigger = (
    <button
      type="button"
      aria-label={ariaLabel || 'Open colour picker'}
      className={[
        'color-wheel-trigger',
        presets && !isCustomSelected ? 'rainbow-trigger' : '',
        presets && isCustomSelected ? 'selected' : '',
        isGlowOff ? 'no-glow' : '',
        modified ? 'modified' : '',
      ].filter(Boolean).join(' ')}
      style={triggerStyle}
      onClick={handleOpen}
    />
  );

  return (
    <>
      {presets ? (
        <div className="color-swatch-row">
          {presets.map((preset) => (
            <button
              key={preset}
              type="button"
              aria-label={`Select colour ${preset}`}
              className={`color-wheel-trigger${currentColor === preset ? ' selected' : ''}`}
              style={{ backgroundColor: preset }}
              onClick={() => handlePreset(preset)}
            />
          ))}
          {pickerTrigger}
        </div>
      ) : pickerTrigger}


      {open && (
        <div
          className="color-wheel-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel || (isGlow ? 'Glow picker' : 'Colour picker')}
          onClick={handleCancel}
        >
          <div
            className="color-wheel-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="color-wheel-modal-header">
              <span>{ariaLabel || (isGlow ? 'Choose a glow' : 'Choose a colour')}</span>
              <button
                type="button"
                className="color-wheel-modal-close"
                aria-label="Close"
                onClick={handleCancel}
              >
                ×
              </button>
            </div>

            <div className="color-wheel-picker-wrap">
              <ChromePicker
                color={draftColor}
                disableAlpha={disableAlpha}
                onChange={handlePickerChange}
              />
            </div>

            {isGlow && (
              <div className="glow-intensity-control">
                <div className="glow-intensity-label">Glow intensity</div>
                <div className="glow-intensity-row">
                  <input
                    type="range"
                    min={GLOW_MIN}
                    max={GLOW_MAX}
                    step={1}
                    value={draftIntensity}
                    onChange={handleIntensitySlider}
                    className="glow-intensity-slider"
                    style={{ accentColor: draftColor }}
                    aria-label="Glow intensity"
                  />
                  <input
                    type="number"
                    min={GLOW_MIN}
                    max={GLOW_MAX}
                    value={intensityText}
                    onChange={handleIntensityText}
                    onBlur={handleIntensityBlur}
                    className="glow-intensity-number"
                    aria-label="Glow intensity value"
                  />
                  <span className="glow-intensity-unit">%</span>
                </div>
              </div>
            )}

            <div className="color-wheel-modal-actions">
              <button
                type="button"
                className="color-wheel-cancel-btn"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="color-wheel-confirm-btn"
                onClick={handleConfirm}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
